import { Calc } from "../math/Calc.js";
import { Matrix3d } from "./Matrix3d.js";
import { IPoint3dValue, Point3d } from "./Point3d.js";
import { Quad } from "./Quad.js";
import { ITransformMode } from "./transformmode/ITransformMode.js";
import { TransformMode } from "./transformmode/TransformMode.js";

/**
 * https://drafts.csswg.org/cssom-view/#geometry
 */
export class Transform {
    //if no mode is given, then
    //use the most lightweight mode
    public static DEFAULT: ITransformMode = TransformMode.Rectangle;

    private static _transform: Transform = new Transform();
    private static _temp: Matrix3d = new Matrix3d();

    public element: HTMLElement = null;
    public origin: Point3d = new Point3d();
    public matrix: Matrix3d = new Matrix3d();
    public sceneTransform: Matrix3d = new Matrix3d();
    public inverseSceneTransform: Matrix3d = new Matrix3d();
    public style: CSSStyleDeclaration = null;
    public mode: ITransformMode = null;

    constructor(element?: HTMLElement, mode?: ITransformMode) {
        this.mode = mode;
        if (element)
            this.update(element, mode);
    }

    public static create(element?: HTMLElement, mode?: ITransformMode): Transform {
        return new Transform(element, mode);
    }

    public update(element?: HTMLElement, mode?: ITransformMode): Transform {
        if (!element)
            return this.clear();

        this.element = element;

        mode = mode || this.mode || Transform.DEFAULT;

        var style = window.getComputedStyle(element);
        this.style = style;

        // var origin = this.origin;
        // var spl = style.transformOrigin.split(" ");
        // origin.x = Calc.relation(spl[0], element.offsetWidth, 0);
        // origin.y = Calc.relation(spl[1], element.offsetHeight, 0);
        // origin.z = Calc.parse(spl[1], 0);

        Transform.origin(element, style, this.origin);

        this.matrix.setCss(style.transform);

        this.sceneTransform.identity();
        mode.extract(this, this.sceneTransform);
        this.sceneTransform.invert(this.inverseSceneTransform);

        return this;
    }

    public prependPosition(position: IPoint3dValue): Transform {
        this.matrix.invert(Transform._temp);
        this.matrix.prependPosition(position);
        this.sceneTransform.append(Transform._temp);
        this.sceneTransform.append(this.matrix);
        this.sceneTransform.invert(this.inverseSceneTransform);
        return this;
    }

    public prepend(matrix: Matrix3d): Transform {
        this.matrix.invert(Transform._temp);
        this.matrix.prepend(matrix);
        //"Subtract" the old matrix.
        this.sceneTransform.append(Transform._temp);
        //Prepend the new matrix.
        this.sceneTransform.append(this.matrix);
        this.sceneTransform.invert(this.inverseSceneTransform);

        return this;
    }

    public append(matrix: Matrix3d): Transform {
        this.matrix.invert(Transform._temp);
        this.matrix.append(matrix);
        //"Subtract" the old matrix.
        this.sceneTransform.prepend(Transform._temp);
        //Prepend the new matrix.
        this.sceneTransform.prepend(this.matrix);
        this.sceneTransform.invert(this.inverseSceneTransform);

        return this;
    }

    public clear(): Transform {
        this.element = null;
        this.matrix.identity();

        return this;
    }

    public localToLocalPoint(
        to: Transform,
        pt: IPoint3dValue,
        ret: Point3d = new Point3d()): Point3d {

        return this.localToLocal(to, pt.x, pt.y, pt.z, ret);
    }

    public localToLocalQuad(
        to: Transform,
        quad: Quad,
        ret: Quad = new Quad()): Quad {

        this.localToLocalPoint(to, quad.p1, ret.p1);
        this.localToLocalPoint(to, quad.p2, ret.p2);
        this.localToLocalPoint(to, quad.p3, ret.p3);
        this.localToLocalPoint(to, quad.p4, ret.p4);
        return ret;
    }

    public localToLocal(
        to: Transform,
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d()): Point3d {

        ret.setTo(x, y, z);
        var gl = this.toGlobalPoint(ret, ret);
        return to.toLocalPoint(gl, ret);
    }

    public toLocalPoint(
        point: IPoint3dValue,
        ret: Point3d = new Point3d()): Point3d {

        return this.toLocal(point.x, point.y, point.z, ret);
    }

    public toLocalQuad(
        quad: Quad,
        ret: Quad = new Quad()): Quad {

        this.toLocalPoint(quad.p1, ret.p1);
        this.toLocalPoint(quad.p2, ret.p2);
        this.toLocalPoint(quad.p3, ret.p3);
        this.toLocalPoint(quad.p4, ret.p4);
        return ret;
    }

    public toLocal(
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d()): Point3d {
        ret.setTo(x, y, z);
        //unproject from parent to child
        this.inverseSceneTransform.unproject(ret, ret);
        return ret;
    }

    public toLocalDelta(
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d()): Point3d {
        ret.setTo(x, y, z);
        this.inverseSceneTransform.deltaTransform(ret, ret);
        return ret;
    }

    public toGlobalPoint(
        point: IPoint3dValue,
        ret: Point3d = new Point3d()): Point3d {
        return this.toGlobal(point.x, point.y, point.z, ret);
    }

    public toGlobalQuad(
        quad: Quad,
        ret: Quad = new Quad()): Quad {
        this.toGlobalPoint(quad.p1, ret.p1);
        this.toGlobalPoint(quad.p2, ret.p2);
        this.toGlobalPoint(quad.p3, ret.p3);
        this.toGlobalPoint(quad.p4, ret.p4);
        return ret;
    }

    public toGlobal(
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d()): Point3d {
        ret.setTo(x, y, z);
        //project from child to parent
        this.sceneTransform.project(ret, ret);
        return ret;
    }

    public toGlobalDelta(
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d()): Point3d {
        ret.setTo(x, y, z);
        this.sceneTransform.deltaTransform(ret, ret);
        return ret;
    }

    public static toGlobal(
        element: HTMLElement,
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d(),
        mode: ITransformMode = this.DEFAULT): Point3d {
        this._transform.update(element, mode);
        return this._transform.toGlobal(x, y, z, ret);
    }

    public static toGlobalDelta(
        element: HTMLElement,
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d(),
        mode: ITransformMode = this.DEFAULT): Point3d {
        this._transform.update(element, mode);
        return this._transform.toGlobalDelta(x, y, z, ret);
    }

    public static toLocal(
        element: HTMLElement,
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d(),
        mode: ITransformMode = this.DEFAULT): Point3d {
        this._transform.update(element, mode);
        return this._transform.toLocal(x, y, z, ret);
    }

    public static toLocalDelta(
        element: HTMLElement,
        x: number,
        y: number,
        z: number = 0,
        ret: Point3d = new Point3d(),
        mode: ITransformMode = this.DEFAULT): Point3d {
        this._transform.update(element, mode);
        return this._transform.toLocalDelta(x, y, z, ret);
    }

    public static origin(element: HTMLElement, style: CSSStyleDeclaration = null, target: Point3d = new Point3d()): Point3d {
        if (!style) {
            style = window.getComputedStyle(element);
        }

        var spl = style.transformOrigin.split(" ");
        target.x = Calc.relation(spl[0], element.offsetWidth, 0);
        target.y = Calc.relation(spl[1], element.offsetHeight, 0);
        target.z = Calc.parse(spl[1], 0);
        return target;
    }

    public dispose(): void {
        this.element = null;
        this.matrix = null;
        this.sceneTransform = null;
        this.inverseSceneTransform = null;
        this.style = null;
        this.mode = null;
    }
}